import { create } from "zustand";
import * as Sentry from "@sentry/react";
import { equals } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import { getConnectedUser } from "@services/user";
import { User, UserSchema } from "@gen/user/v1/user_pb";
import { useChatStore } from "@stores/chatStore";

function syncSentryUser(user: User | null) {
  Sentry.setUser(user ? { id: user.id, email: user.email } : null);
}

const transientCodes = [Code.Unavailable, Code.Unknown, Code.DeadlineExceeded];

let pending: Promise<User | null> | null = null;

interface UserState {
  user: User | null;
  isInitialized: boolean;
  fetchUser: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearUser: () => void;
}

export const useUserStore = create<UserState>((set, get) => ({
  user: null,
  isInitialized: false,
  fetchUser: async () => {
    if (pending) {
      return;
    }
    if (!get().user && window.location.pathname === "/login") {
      set({ isInitialized: true });
      return;
    }
    const req = getConnectedUser();
    pending = req;
    try {
      const user = await req;
      if (pending !== req) return;
      const prev = get().user;
      if (prev && user && equals(UserSchema, prev, user)) return;
      syncSentryUser(user);
      set({ user, isInitialized: true });
    } catch (error) {
      if (pending !== req) return;
      console.error("Error fetching user:", error);
      if (transientCodes.includes(ConnectError.from(error).code)) {
        set({ isInitialized: true });
        return;
      }
      syncSentryUser(null);
      set({ user: null, isInitialized: true });
    } finally {
      if (pending === req) pending = null;
    }
  },
  setUser: (user) => {
    pending = null;
    syncSentryUser(user);
    set({ user, isInitialized: true });
  },
  clearUser: () => {
    pending = null;
    syncSentryUser(null);
    useChatStore.getState().setModule(undefined);
    set({ user: null, isInitialized: true });
  },
}));
